import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ClipboardList, ChevronRight, MapPin, Calendar as CalendarIcon,
    Home, RefreshCw, AlertTriangle,
} from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api } from "@/lib/api";

/**
 * DEV-010B-2 · Engineer Site Surveys list — Web/iPad.
 *
 * Sibling of the Mobile `/installer/surveys` screen. Both consume the
 * same backend endpoint `GET /api/engineers/me/surveys` (DEV-010B-1);
 * cross-device continuity is guaranteed by the single source of truth
 * on the backend. Every row deep-links into
 * `/installer/surveys/:projectId` — the shared capture route — so
 * repeated access never creates a duplicate survey record (the create-
 * or-open POST is idempotent).
 */
const STATUS_META = {
    not_started: { label: "Not started",                dot: "#94A3B8", bg: "#F1F5F9", fg: "#334155", cta: "Open" },
    in_progress: { label: "In progress",                dot: "#F59E0B", bg: "#FEF3C7", fg: "#B45309", cta: "Continue" },
    returned_for_clarification: { label: "Returned for clarification", dot: "#EF4444", bg: "#FEF2F2", fg: "#B91C1C", cta: "Continue" },
    submitted:   { label: "Submitted",                  dot: "#3B82F6", bg: "#EFF6FF", fg: "#1D4ED8", cta: "View" },
    approved:    { label: "Approved",                   dot: "#3E9E2E", bg: "#F1F8EC", fg: "#166534", cta: "View" },
};

function fmtDate(iso) {
    if (!iso) return "No visit scheduled";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "No visit scheduled";
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function InstallerSurveys() {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await api.listMySurveys();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Could not load your surveys.");
            setRows([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        try { await load(); } finally { setRefreshing(false); }
    };

    const loading = rows === null;

    return (
        <InstallerShell>
            <PageContainer>
                <PageHeader
                    icon={ClipboardList}
                    title="Site surveys"
                    subtitle="Digital site surveys for the projects you're assigned to. Actionable surveys are listed first."
                    testid="installer-surveys-page"
                    backTo="/installer"
                    rightSlot={
                        <button
                            type="button"
                            onClick={refresh}
                            disabled={refreshing}
                            data-testid="surveys-refresh"
                            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-white border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F9FAFB] transition-colors disabled:opacity-60"
                        >
                            <RefreshCw size={14} strokeWidth={2} className={refreshing ? "animate-spin" : ""} />
                            {refreshing ? "Refreshing…" : "Refresh"}
                        </button>
                    }
                />

                {loading ? (
                    <div className="py-16 text-center text-sm text-[#4B5565]">Loading surveys…</div>
                ) : error ? (
                    <div className="pe-card p-8 flex flex-col items-center gap-3 text-center" data-testid="surveys-error">
                        <span className="w-12 h-12 rounded-full bg-[#FEF2F2] text-[#B91C1C] flex items-center justify-center">
                            <AlertTriangle size={22} strokeWidth={1.75} />
                        </span>
                        <div className="text-lg text-[#131B2F] font-medium">Couldn&rsquo;t load your surveys</div>
                        <div className="text-sm text-[#4B5565] max-w-md">{error}</div>
                        <button
                            type="button"
                            onClick={load}
                            data-testid="surveys-retry"
                            className="mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110"
                        >
                            Retry
                        </button>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="pe-card p-12 flex flex-col items-center gap-3 text-center" data-testid="surveys-empty">
                        <span className="w-14 h-14 rounded-full bg-[#F1F5F9] text-[#4B5565] flex items-center justify-center">
                            <ClipboardList size={26} strokeWidth={1.5} />
                        </span>
                        <div className="text-lg text-[#131B2F] font-medium">No site surveys yet</div>
                        <div className="text-sm text-[#4B5565] max-w-sm">
                            Surveys will appear here once one is created for a project you&rsquo;re assigned to.
                            You can also open a Site Survey visit from your schedule to start one.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="surveys-list">
                        <div className="text-sm text-[#4B5565]">
                            Showing <span className="font-medium text-[#131B2F]">{rows.length}</span>{" "}
                            {rows.length === 1 ? "survey" : "surveys"} · actionable first.
                        </div>
                        {rows.map((r) => {
                            const meta = STATUS_META[r.status] || STATUS_META.not_started;
                            const dateText = r.appointment_at ? fmtDate(r.appointment_at) : fmtDate(r.survey_date);
                            return (
                                <button
                                    key={r.id}
                                    type="button"
                                    onClick={() => navigate(`/installer/surveys/${r.project_id}`)}
                                    data-testid={`survey-row-${r.project_id}`}
                                    className="w-full text-left pe-card p-5 flex items-center gap-4 hover:bg-[#F9FAFB] transition-colors group"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-base md:text-lg text-[#131B2F] font-medium truncate">
                                                {r.customer_name || "—"}
                                            </span>
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full"
                                                style={{ backgroundColor: meta.bg, color: meta.fg }}
                                                data-testid={`survey-status-${r.project_id}`}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.dot }} />
                                                {meta.label}
                                            </span>
                                        </div>
                                        {r.address && (
                                            <div className="flex items-center gap-1 text-sm text-[#4B5565] mt-1">
                                                <MapPin size={12} strokeWidth={1.75} className="shrink-0" />
                                                <span className="truncate">{r.address}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3 text-xs text-[#4B5565] mt-2 flex-wrap">
                                            <span className="inline-flex items-center gap-1">
                                                <CalendarIcon size={12} strokeWidth={1.75} />
                                                {dateText}
                                            </span>
                                            {r.elevation_count > 0 && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Home size={12} strokeWidth={1.75} />
                                                    {r.elevation_count} elevation{r.elevation_count === 1 ? "" : "s"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-3 flex items-center gap-3">
                                            <div className="flex-1 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden max-w-md">
                                                <div
                                                    className="h-full bg-[#3E9E2E] transition-all"
                                                    style={{ width: `${r.progress || 0}%` }}
                                                    data-testid={`survey-progress-${r.project_id}`}
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-[#131B2F] tabular-nums w-10 text-right">
                                                {r.progress || 0}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="shrink-0 flex items-center gap-2">
                                        <span className="hidden md:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-[#131B2F] text-white group-hover:brightness-110 transition-all">
                                            {meta.cta} <ChevronRight size={14} strokeWidth={2.25} />
                                        </span>
                                        <ChevronRight size={18} className="md:hidden text-[#94A3B8] group-hover:text-[#131B2F] transition-colors" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </PageContainer>
        </InstallerShell>
    );
}
